import React, { useEffect, useState } from 'react';
import './ContentEditor.scss';
import {
  FaFont,
  FaRegImage,
  FaPlus,
  FaTimes,
  FaExpandAlt,
  FaExpand,
  FaHeading,
  FaTextHeight,
  FaAlignLeft,
  FaSubscript,
  FaLevelDownAlt,
  FaIndent,
  FaAngleDoubleRight,
  FaStream,
  FaListAlt,
  FaOutdent,
  FaBold
} from 'react-icons/fa';
import {
  AddPageContentBlockRequest,
  AddPageContentRequest,
  AddPageContentRowRequest,
  BlockTypeEnum,
  PageContentResponse,
  TextStyleEnum
} from '../models/api';
import { v4 as uuidv4 } from 'uuid';
import Button, { ActionResult } from './Button';
import { updateContents } from '../services/page.service';
import useSaveShortcut from '../hooks/useSaveShortcut';
import Feedback from './Feedback';
import FileUpload from './FileUpload';
import { useUnsavedChanges } from '../contexts/UnsavedChangesProvider';
import MarkdownEditor from './MarkdownEditor';

type ContentEditorProps = {
  reference: string;
  pageContents: PageContentResponse;
  setMessage: (message: string) => void;
  setErrors: (errors: string[]) => void;
};
type RowProps = {
  row: RowModel;
  rowIndex: number;
  addBlock: (
    rowIndex: number,
    blockPosition: number,
    type: BlockTypeEnum
  ) => void;
  removeBlock: (rowIndex: number, blockIndex: number) => void;
  updateBlock: (
    rowIndex: number,
    blockIndex: number,
    model: BlockModel
  ) => void;
  toggleExandable: (rowIndex: number) => void;
};
type BlockRowProps = {
  row: RowModel;
  rowIndex: number;
  block: BlockModel;
  blockIndex: number;
  addBlock: (
    rowIndex: number,
    blockPosition: number,
    type: BlockTypeEnum
  ) => void;
  removeBlock: (rowIndex: number, blockIndex: number) => void;
  updateBlock: (
    rowIndex: number,
    blockIndex: number,
    model: BlockModel
  ) => void;
};
type BlockProps = {
  block: BlockModel;
  blockIndex: number;
  removeBlock: (blockIndex: number) => void;
  updateBlock: (blockIndex: number, model: BlockModel) => void;
};
type AddBlockButtonProps = {
  addBlock: (type: BlockTypeEnum) => void;
};

type ContentRowProps = {
  row: RowModel;
  index: number;
  addRow: (row: RowModel, rowIndex: number) => void;
  addBlock: (
    rowIndex: number,
    blockPosition: number,
    type: BlockTypeEnum
  ) => void;
  removeBlock: (rowIndex: number, blockIndex: number) => void;
  updateBlock: (
    rowIndex: number,
    blockIndex: number,
    model: BlockModel
  ) => void;
  toggleExandable: (rowIndex: number) => void;
};

interface RowModel {
  id: string;
  isExpandable: boolean;
  blocks: BlockModel[];
}
interface BlockModel {
  id: string;
  blockType: BlockTypeEnum;
  text?: string | null;
  imageName?: string | null;
  imageContents?: File | null;
  imageContentsData?: string | null;
  getFromServer?: boolean;
  textStyle?: TextStyleEnum | null;
}

type TextToolbarProps = {
  type: TextStyleEnum | null | undefined;
  handleStyleChange: (type: TextStyleEnum) => void;
};

const TextToolbar: React.FC<TextToolbarProps> = ({
  type,
  handleStyleChange
}) => {
  return (
    <div className="toolbar">
      <Button
        title="Display as a header"
        type={type === TextStyleEnum.Header ? 'Secondary' : 'Outline'}
        onClick={() => handleStyleChange(TextStyleEnum.Header)}
        className={type === TextStyleEnum.Header ? 'active' : ''}
      >
        <FaHeading title="Display as a header" />1
      </Button>
      <Button
        title="Display as a sub-header"
        type={type === TextStyleEnum.SubHeader ? 'Secondary' : 'Outline'}
        onClick={() => handleStyleChange(TextStyleEnum.SubHeader)}
        className={type === TextStyleEnum.SubHeader ? 'active' : ''}
      >
        <FaHeading title="Display as a sub-header" />2
      </Button>
      <Button
        title="Display as basic text"
        type={type === TextStyleEnum.Basic ? 'Secondary' : 'Outline'}
        onClick={() => handleStyleChange(TextStyleEnum.Basic)}
        className={!type || type === TextStyleEnum.Basic ? 'active' : ''}
      >
        <FaAlignLeft title="Display as basic text" />
      </Button>
    </div>
  );
};

const AddBlockButton: React.FC<AddBlockButtonProps> = ({ addBlock }) => {
  const [showOptions, setShowOptions] = useState(false);

  const add = (type: BlockTypeEnum) => {
    setShowOptions(false);
    addBlock(type);
  };

  return (
    <div className="add-block-group">
      <Button
        type="Secondary"
        onClick={() => setShowOptions((p) => !p)}
        aria-label="Add content"
      >
        <FaPlus />
      </Button>
      {showOptions && (
        <div className="button-group">
          <button
            type="button"
            onClick={() => add(BlockTypeEnum.Text)}
            aria-label="Add text block"
          >
            <FaFont />
          </button>
          <button
            type="button"
            onClick={() => add(BlockTypeEnum.Image)}
            aria-label="Add image block"
          >
            <FaRegImage />
          </button>
        </div>
      )}
    </div>
  );
};

type AddRowButtonProps = {
  setBody: (type: BlockTypeEnum) => void;
};
const AddRowButton: React.FC<AddRowButtonProps> = ({ setBody }) => {
  const [showOptions, setShowOptions] = useState(false);

  const add = (type: BlockTypeEnum) => {
    setShowOptions(false);
    setBody(type);
  };

  return (
    <div className="add-block-group">
      <Button
        type="Secondary"
        onClick={() => setShowOptions((p) => !p)}
        aria-label="Add content"
      >
        <FaPlus />
      </Button>
      {showOptions && (
        <div className="button-group">
          <button
            type="button"
            onClick={() => add(BlockTypeEnum.Text)}
            aria-label="Add text block"
          >
            <FaFont />
          </button>
          <button
            type="button"
            onClick={() => add(BlockTypeEnum.Image)}
            aria-label="Add image block"
          >
            <FaRegImage />
          </button>
        </div>
      )}
    </div>
  );
};

const BlockRow: React.FC<BlockRowProps> = ({
  rowIndex,
  addBlock,
  removeBlock,
  updateBlock,
  block,
  blockIndex
}) => {
  return (
    <>
      <div className="row-divider">
        <AddBlockButton
          addBlock={(type) => addBlock(rowIndex, blockIndex, type)}
        />
      </div>
      {block.blockType === BlockTypeEnum.Text && (
        <TextBlock
          block={block}
          blockIndex={blockIndex}
          updateBlock={(blockIndex, block) =>
            updateBlock(rowIndex, blockIndex, block)
          }
          removeBlock={(blockIndex: number) =>
            removeBlock(rowIndex, blockIndex)
          }
        />
      )}
      {block.blockType === BlockTypeEnum.Image && (
        <ImageBlock
          block={block}
          blockIndex={blockIndex}
          updateBlock={(blockIndex, block) =>
            updateBlock(rowIndex, blockIndex, block)
          }
          removeBlock={(blockIndex: number) =>
            removeBlock(rowIndex, blockIndex)
          }
        />
      )}
    </>
  );
};

const Row: React.FC<RowProps> = ({
  row,
  rowIndex,
  addBlock,
  removeBlock,
  updateBlock,
  toggleExandable
}) => {
  return (
    <div className="row" key={`inner-row-${row.id}`}>
      {row.blocks.map((p, i) => (
        <BlockRow
          addBlock={addBlock}
          block={p}
          blockIndex={i}
          removeBlock={removeBlock}
          row={row}
          rowIndex={rowIndex}
          updateBlock={updateBlock}
          key={`row-block-${p.id}`}
        />
      ))}
      <div
        className={`row-divider ${row.blocks.length === 0 ? 'initial' : ''}`}
      >
        <AddBlockButton
          addBlock={(type) => addBlock(rowIndex, row.blocks.length, type)}
        />
      </div>
      <div className="options">
        <button onClick={() => toggleExandable(rowIndex)} type="button">
          {row.isExpandable ? (
            <FaExpandAlt title="Content is viewed by expanding the row" />
          ) : (
            <FaFont title="Content shown by default" />
          )}
        </button>
      </div>
    </div>
  );
};

const TextBlock: React.FC<BlockProps> = ({
  block,
  blockIndex,
  removeBlock,
  updateBlock
}) => {
  const [body, setBody] = useState(block.text ?? '');

  return (
    <div className="column">
      <button
        className="delete-icon"
        onClick={() => removeBlock(blockIndex)}
        aria-label="Delete content"
      >
        <FaTimes />
      </button>
      <div className="text-input">
        <TextToolbar
          handleStyleChange={(e) => {
            updateBlock(blockIndex, { ...block, textStyle: e });
          }}
          type={block.textStyle}
        />
        <MarkdownEditor
          value={body}
          onChange={(value) => {
            setBody(value);
            updateBlock(blockIndex, { ...block, text: value });
          }}
          placeholder="Set your text here..."
          isHeader={block.textStyle !== TextStyleEnum.Basic}
        />
      </div>
    </div>
  );
};
const ImageBlock: React.FC<BlockProps> = ({
  block,
  blockIndex,
  removeBlock,
  updateBlock
}) => {
  const [selectedImage, setSelectedImage] = useState<File | null | undefined>(
    block.imageContents
  );
  const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null>(
    null
  );

  const handleImageChange2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
    }
  };
  const handleImageChange = (file: File | undefined) => {
    if (file) {
      setSelectedImage(file);
    }
  };

  useEffect(() => {
    if (selectedImage) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          setImagePreview(reader.result);
          updateBlock(blockIndex, {
            ...block,
            imageContents: selectedImage,
            imageContentsData: reader.result.toString().split(',')[1],
            imageName: selectedImage.name
          });
        } else {
          console.log("Can't read that image?");
        }
      };
      reader.readAsDataURL(selectedImage);
    }
  }, [selectedImage]);

  return (
    <div className="column">
      <button
        className="delete-icon"
        onClick={() => removeBlock(blockIndex)}
        aria-label="Delete content"
      >
        <FaTimes />
      </button>
      {!(selectedImage || block.getFromServer) && (
        <div className="image-input">
          {/* <input type="file" accept="image/*" onChange={handleImageChange} /> */}
          <FileUpload accept="image/*" onChange={handleImageChange} />
        </div>
      )}
      {(selectedImage || block.getFromServer) && (
        <div className="image-preview">
          <label>{block.imageName}</label>
          <div>
            <img
              src={
                block.getFromServer
                  ? `${process.env.REACT_APP_API_URL}/api/images/${block.imageContentsData?.replace(/\//g, '|')}`
                  : (imagePreview as string)
              }
              alt="Selected"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const ContentRow: React.FC<ContentRowProps> = ({
  row,
  index,
  addRow,
  addBlock,
  removeBlock,
  updateBlock,
  toggleExandable
}) => {
  return (
    <>
      <div className="divider">
        <AddRowButton
          setBody={(p) =>
            addRow(
              {
                id: uuidv4(),
                isExpandable: false,
                blocks: [
                  {
                    id: uuidv4(),
                    text: '',
                    blockType: p,
                    textStyle: TextStyleEnum.Basic
                  }
                ]
              },
              index
            )
          }
        />
      </div>
      <Row
        key={`row-${row.id}`}
        row={row}
        rowIndex={index}
        addBlock={addBlock}
        removeBlock={removeBlock}
        updateBlock={updateBlock}
        toggleExandable={toggleExandable}
      />
    </>
  );
};

const ContentEditor: React.FC<ContentEditorProps> = ({
  reference,
  pageContents,
  setMessage,
  setErrors
}) => {
  const { hasUnsavedChanges, setHasUnsavedChanges } = useUnsavedChanges();
  const [isSaving, setIsSaving] = useState(false);

  const convert = (pageContents: PageContentResponse) => {
    return (
      pageContents.rows?.map(
        (p) =>
          ({
            id: p.reference,
            isExpandable: p.isExpandable,
            blocks: p.blocks.map(
              (q) =>
                ({
                  id: q.reference,
                  blockType: q.blockType,
                  text: q.text,
                  imageName: q.fileName,
                  imageContentsData: q.blobName,
                  getFromServer: true,
                  textStyle: q.textStyle
                }) as BlockModel
            )
          }) as RowModel
      ) || []
    );
  };
  const [body, setBody] = useState<RowModel[]>([]);

  useEffect(() => {
    setBody(convert(pageContents));
    setHasUnsavedChanges(false);
  }, [pageContents]);

  useEffect(() => {
    setErrors([]);
  }, [reference]);

  const addBlock = (
    rowIndex: number,
    blockPosition: number,
    type: BlockTypeEnum
  ) => {
    setBody((prevBody) => {
      const updatedBody = [...prevBody];
      updatedBody[rowIndex] = {
        ...updatedBody[rowIndex],
        blocks: [
          ...updatedBody[rowIndex].blocks.slice(0, blockPosition),
          {
            id: uuidv4(),
            text: ``,
            blockType: type,
            textStyle: TextStyleEnum.Basic
          },
          ...updatedBody[rowIndex].blocks.slice(blockPosition)
        ]
      };
      return updatedBody;
    });
    setHasUnsavedChanges(true);
  };
  const addRow = (rowIndex: number, row: RowModel) => {
    setBody((prevBody) => {
      const updatedBody = [
        ...prevBody.slice(0, rowIndex),
        row,
        ...prevBody.slice(rowIndex)
      ];
      return updatedBody;
    });
    setHasUnsavedChanges(true);
  };
  const removeBlock = (rowIndex: number, blockIndex: number) => {
    setBody((prevBody) => {
      let updatedBody = [...prevBody];
      updatedBody[rowIndex] = {
        ...updatedBody[rowIndex],
        blocks: updatedBody[rowIndex].blocks.filter((_, i) => i !== blockIndex)
      };
      if (updatedBody[rowIndex].blocks.length === 0) {
        updatedBody = updatedBody.filter((_, i) => i !== rowIndex);
      }
      return updatedBody;
    });
    setHasUnsavedChanges(true);
  };
  const updateBlock = (
    rowIndex: number,
    blockIndex: number,
    model: BlockModel
  ) => {
    setBody((prevBody) => {
      const updatedBody = [...prevBody];
      updatedBody[rowIndex] = {
        ...updatedBody[rowIndex],
        blocks: updatedBody[rowIndex].blocks.map((p, i) =>
          i === blockIndex ? model : p
        )
      };
      return updatedBody;
    });
    setHasUnsavedChanges(true);
  };
  const toggleExandable = (rowIndex: number) => {
    setBody((prevBody) => {
      const updatedBody = [...prevBody];
      updatedBody[rowIndex] = {
        ...updatedBody[rowIndex],
        isExpandable: !updatedBody[rowIndex].isExpandable
      };
      return updatedBody;
    });
    setHasUnsavedChanges(true);
  };
  const doSave = async () => {
    if (isSaving) {
      return null;
    }
    setIsSaving(true);

    const model: AddPageContentRequest = {
      rows: body.map(
        (row) =>
          ({
            id: row.id,
            isExpandable: row.isExpandable,
            blocks: row.blocks.map(
              (block) =>
                ({
                  id: block.id,
                  blockType: block.blockType,
                  text: block.text,
                  altText: '',
                  imageName: block.imageName,
                  imageContents: block.imageContentsData,
                  getFromServer: block.getFromServer,
                  textStyle: block.textStyle
                }) as AddPageContentBlockRequest
            )
          }) as AddPageContentRowRequest
      )
    };

    const result = await updateContents(reference, model);

    let feedback: ActionResult;

    if (result.success) {
      setMessage('Saved');
      setHasUnsavedChanges(false);
      setErrors([]);
      feedback = 'Success';
    } else {
      setMessage('');
      setErrors(['There was a problem, changes have not been saved']);
      feedback = 'Error';
    }
    setIsSaving(false);

    return feedback;
  };

  useSaveShortcut(doSave);

  return (
    <form className="content-form-full">
      {body.map((p, index) => (
        <ContentRow
          key={`contentRow-${p.id}`}
          row={p}
          index={index}
          addBlock={addBlock}
          removeBlock={removeBlock}
          updateBlock={updateBlock}
          toggleExandable={toggleExandable}
          addRow={(row, rowIndex) => addRow(rowIndex, row)}
        />
      ))}
      <div className="divider">
        <AddRowButton
          setBody={(p) => {
            setBody([
              ...body,
              {
                id: uuidv4(),
                isExpandable: false,
                blocks: [
                  {
                    id: uuidv4(),
                    text: '',
                    blockType: p,
                    textStyle: TextStyleEnum.Basic
                  }
                ]
              }
            ]);
            setHasUnsavedChanges(true);
          }}
        />
      </div>
      <div
        className="button-container single-button"
        style={{ marginRight: '20px' }}
      >
        <Button
          isLoading={isSaving}
          onClick={doSave}
          loadingText="Saving"
          successText="Saved"
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default ContentEditor;
